// ---------------------------------------------------------------------------
// AppLock — Tier 2: Encrypted storage adapter
// ---------------------------------------------------------------------------
import { encrypt, decrypt, isEncryptedPayload, AppLockCryptoError, } from '../crypto/crypto-adapter.js';
import { LocalStorageBackend } from './storage-backend.js';
import { META_KEYS } from '../core/config.js';
/**
 * Encrypted (Tier 2) storage.
 *
 * Same `StorageAdapter` interface as passthrough, but transparently
 * encrypts/decrypts data using AES-256-GCM via the held CryptoKey.
 *
 * Key decisions:
 * - If `encryptedKeys` is set in config, only those keys are encrypted.
 * - All keys in `cleartextKeys` are always stored in cleartext.
 * - If neither is set, all non-meta keys are encrypted.
 */
export class EncryptedStorage {
    constructor(keyLifecycle, config, backend) {
        this.keyLifecycle = keyLifecycle;
        this.config = config;
        this.backend = backend ?? new LocalStorageBackend();
    }
    async save(key, data) {
        const serialized = JSON.stringify(data);
        if (this.shouldEncrypt(key)) {
            const cryptoKey = this.keyLifecycle.get(); // throws if locked
            const payload = await encrypt(cryptoKey, serialized);
            await this.backend.setItem(key, JSON.stringify(payload));
        }
        else {
            await this.backend.setItem(key, serialized);
        }
    }
    async load(key) {
        const raw = await this.backend.getItem(key);
        if (raw === null)
            return null;
        if (this.shouldEncrypt(key) && isEncryptedPayload(raw)) {
            const cryptoKey = this.keyLifecycle.get(); // throws if locked
            const payload = JSON.parse(raw);
            try {
                const decrypted = await decrypt(cryptoKey, payload);
                return JSON.parse(decrypted);
            }
            catch (err) {
                if (err instanceof AppLockCryptoError)
                    throw err;
                // JSON parse failure on decrypted content
                throw new AppLockCryptoError('tamper', `Failed to parse decrypted data for key "${key}".`);
            }
        }
        try {
            return JSON.parse(raw);
        }
        catch {
            return raw;
        }
    }
    async remove(key) {
        await this.backend.removeItem(key);
    }
    async keys() {
        const allKeys = await this.backend.getAllKeys();
        return allKeys.filter((key) => !META_KEYS.includes(key));
    }
    async clear() {
        const allKeys = await this.backend.getAllKeys();
        const toRemove = allKeys.filter((key) => !META_KEYS.includes(key));
        for (const key of toRemove) {
            await this.backend.removeItem(key);
        }
    }
    // -----------------------------------------------------------------------
    // Migration helpers (used by the main AppLock class)
    // -----------------------------------------------------------------------
    /**
     * Encrypt all currently cleartext data with the given key.
     * Used when enabling encryption on existing data.
     */
    async migrateToEncrypted(key) {
        const allKeys = await this.keys();
        for (const storageKey of allKeys) {
            if (!this.shouldEncrypt(storageKey))
                continue;
            const raw = await this.backend.getItem(storageKey);
            if (raw === null || isEncryptedPayload(raw))
                continue;
            // Data is cleartext — encrypt it
            const payload = await encrypt(key, raw);
            await this.backend.setItem(storageKey, JSON.stringify(payload));
        }
    }
    /**
     * Decrypt all encrypted data back to cleartext.
     * Used when removing the lock password.
     */
    async migrateToCleartext(key) {
        const allKeys = await this.keys();
        for (const storageKey of allKeys) {
            const raw = await this.backend.getItem(storageKey);
            if (raw === null || !isEncryptedPayload(raw))
                continue;
            const payload = JSON.parse(raw);
            const decrypted = await decrypt(key, payload);
            await this.backend.setItem(storageKey, decrypted);
        }
    }
    /**
     * Re-encrypt all data from an old key to a new key.
     * Used during password change.
     */
    async reEncrypt(oldKey, newKey) {
        const allKeys = await this.keys();
        for (const storageKey of allKeys) {
            if (!this.shouldEncrypt(storageKey))
                continue;
            const raw = await this.backend.getItem(storageKey);
            if (raw === null || !isEncryptedPayload(raw))
                continue;
            // Decrypt with old key
            const payload = JSON.parse(raw);
            const plaintext = await decrypt(oldKey, payload);
            // Re-encrypt with new key
            const newPayload = await encrypt(newKey, plaintext);
            await this.backend.setItem(storageKey, JSON.stringify(newPayload));
        }
    }
    // -----------------------------------------------------------------------
    // Internal
    // -----------------------------------------------------------------------
    shouldEncrypt(key) {
        // Meta-keys are never encrypted
        if (META_KEYS.includes(key))
            return false;
        // Explicit cleartext keys
        if (this.config.cleartextKeys.includes(key))
            return false;
        // If an explicit encrypted key list is set, only encrypt those
        if (this.config.encryptedKeys) {
            return this.config.encryptedKeys.includes(key);
        }
        // Default: encrypt everything else
        return true;
    }
}
//# sourceMappingURL=encrypted.js.map